<template>
    <div class="flex flex-col items-center justify-center h-full">
        <div
            v-if="isSongQuiz"
            :style="[embedToken ? { transform: 'scale(.8)' } : {}]"
            class="box-container bg-white py-6 px-12 shadow-lg text-center shadow-text-none rounded"
        >
            <i class="fas fa-volume-up font-size-50 mb-5 main-color"></i>
            <h1 class="mt-0 md:font-size-22 font-size-18 mb-4 warning-title">
                {{ $t('playalone_has_sound') }}<br />
                {{ $t('playalone_pump_volume') }}
            </h1>
            <div @click="onClickPlay" id="playWithSound" class="btn theme-button text-xl font-size-22 font-opensans">
                {{ $t('playalone_button_play') }}
            </div>
        </div>

        <ShareModal
            v-if="showShareModal"
            v-model="quiz.url"
            @close="showShareModal = false"
            @copied="copyLink($event, 'link')"
            :is-front-page="true"
            :sub-title="$t('playalone_share_quiz_modal_not_support_puzzle')"
            :quiz-id="quiz._id.$oid"
        />
    </div>
</template>

<script>
import Background from '@/lib/background.js'
import appConfig from '../app.config.js'
import preloadImageMixin from '@/mixins/preload-image'
import preloadSongMixin from '@/mixins/preload-song'
import UserService from '@/lib/user.js'
import Request from '@/lib/request.js'
import { mapGetters, mapState } from 'vuex'
import helper from '@/lib/helper.js'
import popup from '@/lib/popup.js'
import helperMixins from '@/mixins/helper.js'
import ShareModal from '@/components/PlayAloneShareModal.vue'

export default {
    metaInfo() {
        return {
            link: [
                {
                    rel: 'canonical',
                    href: this.canonicalUrl
                }
            ]
        }
    },

    components: {
        ShareModal
    },

    mixins: [preloadImageMixin, preloadSongMixin, helperMixins],

    computed: {
        ...mapGetters({
            audioSpotify: 'getSpotifyAudio',
            frontpageSetting: 'getFrontPageSetting',
            frontPageBackgroundSet: 'getFrontPageBackgroundSet'
        }),

        ...mapState({
            quizId: state => state.player.quiz._id.$oid,
            quiz: state => state.player.quiz,
            embedToken: state => state.player.embedToken,
            code: state => state.player.quiz.code
        }),

        canonicalUrl() {
            return `${process.env.VUE_APP_ROOT_URL}play/${this.$route.params.code}`
        }
    },

    data() {
        return {
            isSongQuiz: false,
            isMusic: false,
            isSongBG: false,

            isClicked: false,

            storageKey: 'playalone-anonymous-user',
            playalone_ano_user: {},

            isQuizFetched: false,
            showShareModal: false
        }
    },

    created() {
        this.addStyleSheet(this.$route.params.code)

        const url = new URL(location.href)
        const mode = url.searchParams.get('mode')

        this.$store.commit('setPlayMode', mode)
    },

    mounted: function () {
        this.touchActionNone()

        if (this.$route.query.ref && this.$route.query.ref === 'myquiz') {
            this.$store.commit('player/setIsFromMyQuiz', true)
        }

        if (this.$route.query.rp && this.$route.query.rp.length > 5) {
            this.$store.commit('player/setPlayAloneReportToken', this.$route.query.rp)
        }
        if (this.$route.query.user_token) {
            this.playalone_ano_user = atob(this.$route.query.user_token)
            localStorage.setItem(this.storageKey, this.playalone_ano_user)
        }
        if (this.$route.query.embed) {
            this.$store.commit('player/setEmbedToken', this.$route.query.embed)
        }

        if (this.$route.query.isFront) {
            if (this.$route.query.isFront == 1 || this.$route.query.isFront === 'true') {
                this.$store.commit('player/setIsFrontPageQuiz', true)
            } else {
                this.$store.commit('player/setIsFrontPageQuiz', false)
            }
        }

        this.fetchQuestion()
    },

    methods: {
        onClickPlay() {
            if (this.isClicked) {
                return
            }

            this.audioSpotify.src = null
            this.isClicked = true

            this.$router.push({
                name: 'play-alone',
                params: {
                    code: this.$route.params.code
                },
                query: {
                    embed: this.$route.query.embed
                }
            })
        },

        fetchQuestion() {
            let params = { code: this.$route.params.code }
            if (helper.isMessengerWebview()) {
                params = {
                    code: this.$route.params.code,
                    webview: 1
                }
            }
            this.$store
                .dispatch('fetchQuestionByQuizCode', params)
                .then(res => {
                    helper.gTrackEvent('play', {
                        event_category: 'PlayAlone',
                        event_label: `StartPlay_${this.$route.params.code}_single`
                    })

                    if (res.is_frontpage_quiz) {
                        this.$store.commit('player/setIsFrontPageQuiz', res.is_frontpage_quiz)
                    }

                    //check empty puzzle and show dialog
                    if (res.is_puzzle_empty) {
                        this.showShareModal = true
                        return
                    }

                    const mode = new URL(window.location.href).searchParams.get('mode')

                    if (mode && mode == 'preview') {
                        res.inactive_quiz = false
                    }

                    if (res.inactive_quiz) {
                        this.$store.commit('toggleMainModal', false)
                        window.location.href = '/'
                        return
                    }

                    // set play type count
                    const user = new UserService()
                    var data = {
                        quiz_id: res._id.$oid,
                        quiz_type: res.type,
                        quiz_created_by: res.created_by ? res.created_by : 'admin',
                        playing_type: 'play_alone'
                    }

                    if (user.getUser()) {
                        const request = new Request()
                        data.user_id = user.getUser().id

                        request.post(appConfig.api.setLastActivity.replace(/__id__/, user.getUser().id), {
                            user_activity: 'last_play'
                        })

                        request.post(appConfig.api.setUserPlayType.replace(/__id__/, user.getUser().id), data)
                    }

                    // check if song quiz
                    const questionSong = res.questions.filter(question => {
                        return question.spotify_track && question.spotify_track.preview_url
                    })
                    if (questionSong.length > 0) {
                        this.isSongQuiz = true
                    } else {
                        this.onClickPlay()
                    }
                    // end check if song quiz
                    this.preloadQuestionImage(res.questions)
                    this.preloadNextSong(res.questions[0])
                    this.$store.dispatch('saveQuizPlaying', data)

                    let color = res.background_type.length > 0 ? res.background_color : appConfig.defaultBackgroundColor

                    new Background({
                        color: res.theme_name ? res.themes[res.theme_name].background_color : color,
                        image: res.theme_name ? res.themes[res.theme_name].background_image : res.background_image,
                        type: res.background_type,
                        blur: res.background_image_blur,
                        darken: res.background_image_darken,
                        navText: this.frontpageSetting.nav_text,
                        useTheme: res.theme_name ? true : false
                    })

                    this.$store.commit('player/clearCoin')
                    this.$store.dispatch('getQuizRelateCategory', {
                        tags: this.quiz.tags ? this.quiz.tags : [],
                        quiz_id: this.quizId
                    })

                    this.isQuizFetched = false
                })
                .catch(e => {
                    console.log(e)
                    this.$store.commit('toggleMainModal', false)
                    window.location.href = '/'
                })
        },

        copyLink(url, type) {
            url = url || window.shareLink

            if (window.navigator.share) {
                navigator.share({
                    title: 'TheBigQuiz',
                    text: 'TheBigQuiz',
                    url: url
                })
            } else {
                navigator.clipboard
                    .writeText(url)
                    .then(() => {
                        if (type != 'embed') {
                            popup.alert(this.$t('landing_alert_link_copied'), true, { icon: 'success' })
                        } else {
                            popup.alert(this.$t('embed_modal_alert_copied'), true, { icon: 'success' })
                        }
                    })
                    .catch(() => {
                        if (type != 'embed') {
                            popup.alert(this.$t('landing_alert_link_copied_error'), true, { icon: 'error' })
                        } else {
                            popup.alert(this.$t('embed_modal_alert_copied_error'), true, { icon: 'error' })
                        }
                    })
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.box-container {
    min-width: 280px;
}
.main-color {
    color: var(--button-background-color);
}
.warning-title {
    color: var(--button-background-color);
}
.h-full {
    @media (max-width: 767px) {
        height: calc((var(--vh, 1vh) * 100) - 1rem);
    }
}
</style>
